/**
 * Claimflow rule-context schemas + factories.
 *
 * One schema per claimflow page. The schema (`*ContextSchema`) is the
 * allow-list of variable names / types the page exposes via
 * `RuleContextProvider`; the factory (`build*RuleContext`) produces the
 * runtime value bag that matches it. Registering both in a single module
 * guarantees the admin config page and the live provider stay in lockstep.
 */

import type { ContextVariableDescriptor } from '@components/shared/configurable';

import type { ClaimSummary, ProvisionToRecoverRow } from './types';
import type { VeosTaskInfo } from './api/taskService';

// ---------------------------------------------------------------------------
// TaskDetailPage
// ---------------------------------------------------------------------------

/**
 * Allow-list of rule-context variables exposed by `TaskDetailPage`. Keep
 * descriptions one-liners — the admin config page renders them in a
 * tooltip next to each badge.
 */
export const taskDetailContextSchema = {
  task_type: {
    type: 'string',
    description:
      'Canonical ECRAN value of the task, read by id from the veos WS2_GS_SEL_TACHE endpoint whatever its status or assignee (e.g. "Mise en cause", "Déclaration", "Constat"); "AUTRE" (free action) when the claim is opened without a task.',
    sourceField: 'ECRAN',
  },
  task_type_label: {
    type: 'string',
    description:
      'Human-readable task type label from LIBELLE_TYPE_TACHE (e.g. "Email"). Distinct from `task_type`, which is the ECRAN screen value.',
    sourceField: 'LIBELLE_TYPE_TACHE',
  },
  task_classification: {
    type: 'string',
    description:
      'Human-readable classification label that an admin can target from NL.',
    sourceField: 'CLASSEMENT',
  },
  task_classification_label: {
    type: 'string',
    description:
      'Human-readable classification label (e.g. "Réception rapport", "Suivi courtier") — the CLASSEMENT_LIB counterpart of `task_classification`.',
    sourceField: 'CLASSEMENT_LIB',
  },
  claim_reference: {
    type: 'string',
    description: 'NCBT_SIN natural reference of the claim.',
    sourceField: 'NCBT_SIN',
  },
  claim_responsibility_percent: {
    type: 'number',
    description:
      'Numeric responsibility rate parsed from TXRESP_SIN — enables greaterThan / lessThan conditions.',
    sourceField: 'TXRESP_SIN',
  },
  claim_management_type: {
    type: 'string',
    description: 'Management type (e.g. "Corporate" / "Individual") from CB_GESTION.',
    sourceField: 'CB_GESTION',
  },
  total_cost: {
    type: 'number',
    description:
      'Sum of the garanties\' net cost (COUT_CIE of the CD_PSD "TOTAL" rows — poste detail rows excluded) in EUR — useful for threshold rules on claim value.',
  },
  recoverable_provisions_count: {
    type: 'number',
    description:
      'Number of "provisions à encaisser" rows returned by boby-ws2-gs-sel-provision-a-encaisser — `undefined` when the fetch failed, so test "none" with `equals 0` (never `isEmpty`, which is also true for a missing variable and would hide on a fetch error).',
  },
  recoverable_provision_total: {
    type: 'number',
    description:
      'Sum of RECPROV over the "provisions à encaisser" rows, in EUR — `undefined` when the fetch failed.',
    sourceField: 'RECPROV',
  },
  tenant_slug: {
    type: 'string',
    description:
      'Active tenant slug from the URL — enables per-tenant targeting (e.g. SettlementCard defaults for `iga-servicing`).',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Literal union of every key in {@link taskDetailContextSchema}. */
export type TaskDetailContextKey = keyof typeof taskDetailContextSchema;

/**
 * `task_type` of a claim screen opened WITHOUT a task (from the claim search,
 * mahhp 22861): the VEOS free-action screen, so admin rules written for free
 * actions apply.
 */
export const TASKLESS_TASK_TYPE = 'AUTRE';

/**
 * Build the rule-context value bag consumed by `RuleContextProvider` on
 * `TaskDetailPage`. The returned object is a plain value (not a hook) so
 * the page can wrap it in a `useMemo` for referential stability.
 */
export function buildTaskDetailRuleContext(args: {
  taskInfo: VeosTaskInfo | null;
  claimSummary: ClaimSummary | null;
  ncbtSin: string | null;
  totalCost: number;
  /**
   * Rows from `boby-ws2-gs-sel-provision-a-encaisser`, or `null` when the
   * page-level fetch failed. `null` leaves both recoverable_* variables
   * `undefined` so a `hide` rule fails open (the card stays visible).
   */
  provisionsToRecover: ProvisionToRecoverRow[] | null;
  tenantSlug: string | undefined;
  /**
   * `true` when the claim screen has no task (mahhp 22861): `task_type` is then
   * {@link TASKLESS_TASK_TYPE} instead of the (absent) task's ECRAN. Omitted /
   * `false` keeps the task's value — `undefined` while its info is unknown.
   */
  taskless?: boolean;
}): Record<TaskDetailContextKey, unknown> {
  const {
    taskInfo,
    claimSummary,
    ncbtSin,
    totalCost,
    provisionsToRecover,
    tenantSlug,
    taskless = false,
  } = args;
  const claimResponsibilityPercent = claimSummary
    ? Number(claimSummary.responsibility.replace('%', ''))
    : undefined;
  // RECPROV arrives as a number or a numeric string ('500.00'), and can be
  // negative. Anything non-numeric counts as 0 so one bad row never turns the
  // whole total into NaN (which no comparison operator can match).
  const recoverableProvisionTotal = provisionsToRecover?.reduce((acc, row) => {
    const amount = Number(row.RECPROV);
    return acc + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  return {
    task_type: taskless ? TASKLESS_TASK_TYPE : taskInfo?.ECRAN,
    task_type_label: taskInfo?.LIBELLE_TYPE_TACHE,
    // A task with no classification comes back with null CLASSEMENT* — read as
    // a missing variable (`undefined`), never as the value `null`.
    task_classification: taskInfo?.CLASSEMENT ?? undefined,
    task_classification_label: taskInfo?.CLASSEMENT_LIB ?? undefined,
    claim_reference: ncbtSin,
    claim_responsibility_percent: claimResponsibilityPercent,
    claim_management_type: claimSummary?.type,
    total_cost: totalCost,
    recoverable_provisions_count: provisionsToRecover?.length,
    recoverable_provision_total: recoverableProvisionTotal,
    tenant_slug: tenantSlug,
  };
}

// ---------------------------------------------------------------------------
// DashboardPage
// ---------------------------------------------------------------------------

/**
 * Allow-list of rule-context variables exposed by `DashboardPage`.
 */
export const dashboardContextSchema = {
  is_spoofed_session: {
    type: 'boolean',
    description:
      'True when the admin is impersonating a claimflow session via the spoof flow.',
  },
  tenant_slug: {
    type: 'string',
    description: 'Active tenant slug from the URL — enables per-tenant targeting.',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Literal union of every key in {@link dashboardContextSchema}. */
export type DashboardContextKey = keyof typeof dashboardContextSchema;

/**
 * Build the rule-context value bag consumed by `RuleContextProvider` on
 * `DashboardPage`.
 */
export function buildDashboardRuleContext(args: {
  spoofedSessionId: number | null;
  tenantSlug: string | undefined;
}): Record<DashboardContextKey, unknown> {
  const { spoofedSessionId, tenantSlug } = args;
  return {
    is_spoofed_session: spoofedSessionId != null,
    tenant_slug: tenantSlug,
  };
}

// ---------------------------------------------------------------------------
// ConfigVersioningPage (Configuration & Backups)
// ---------------------------------------------------------------------------

/**
 * Allow-list of rule-context variables exposed by `ConfigVersioningPage`. The
 * Config History / Backups cards render tenant-agnostic self-service config
 * tables, so the only useful conditional target is the active tenant slug —
 * static layout rules (rename / hide / reorder columns) need no context at all.
 */
export const configContextSchema = {
  tenant_slug: {
    type: 'string',
    description: 'Active tenant slug from the URL — enables per-tenant targeting.',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

/** Literal union of every key in {@link configContextSchema}. */
export type ConfigContextKey = keyof typeof configContextSchema;

/**
 * Build the rule-context value bag consumed by `RuleContextProvider` on
 * `ConfigVersioningPage`.
 */
export function buildConfigRuleContext(args: {
  tenantSlug: string | undefined;
}): Record<ConfigContextKey, unknown> {
  return {
    tenant_slug: args.tenantSlug,
  };
}
